import React, { useCallback } from 'react';
import Button from '@/components/UI/Button';
import FormSelect from '@/components/UI/FormSelect';
import FormNumberInput from '@/components/UI/FormNumberInput';
import Modal from '@/components/UI/Modal';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { CategoryPositionRule } from '@einhaus/project314-types';

interface CategoryRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    portfolioId: string | undefined;
    editingRuleId: string | null;
    newRule: Partial<CategoryPositionRule>;
    setNewRule: React.Dispatch<React.SetStateAction<Partial<CategoryPositionRule>>>;
    rules: CategoryPositionRule[];
    setRules: React.Dispatch<React.SetStateAction<CategoryPositionRule[]>>;
}

const CategoryRuleModal: React.FC<CategoryRuleModalProps> = ({
    isOpen,
    onClose,
    portfolioId,
    editingRuleId,
    newRule,
    setNewRule,
    rules,
    setRules
}) => {
    const queryClient = useQueryClient();

    // Mutation for saving category rule (add/edit)
    const saveCategoryRuleMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Category rule saved successfully',
            fallbackErrorMessage: 'Failed to save category rule'
        }
    );

    const handleSaveCategoryRule = useCallback(async () => {
        if (!portfolioId) return;

        if (!newRule.categoryLimitDirection || !newRule.categoryLimitType || !newRule.categoryLimitMaximumType || !newRule.value) {
            showToast('Please fill in all required fields', 'error');
            return;
        }

        const ruleId = editingRuleId ?? Date.now().toString();

        const completeRule = {
            id: ruleId,
            categoryLimitDirection: newRule.categoryLimitDirection,
            categoryLimitType: newRule.categoryLimitType,
            categoryLimitMaximumType: newRule.categoryLimitMaximumType,
            value: newRule.value
        };

        const previousRules = rules;

        const updatedRules =
            editingRuleId == null
                ? [...previousRules, completeRule]
                : previousRules.map((existing) => (existing.id === ruleId ? completeRule : existing));

        // Optimistically update UI
        setRules(updatedRules);
        onClose();

        try {
            await saveCategoryRuleMutation.mutateAsync({
                portfolioId: parseInt(portfolioId),
                settingType: 'categoryLimit',
                value: completeRule
            });
        } catch {
            setRules(previousRules);
        }
    }, [portfolioId, editingRuleId, newRule, rules, saveCategoryRuleMutation, onClose, setRules]);

    return (
        <Modal
            isOpen={isOpen}
            title={editingRuleId == null ? 'Add Category Position Limit Rule' : 'Edit Category Position Limit Rule'}
            onClose={onClose}
        >
            <div className="space-y-4">
                <div className="mb-4">
                    <FormSelect
                        id="categoryRuleDirection"
                        label="Underlying exposure"
                        options={[
                            { value: 'long', label: 'Bullish' },
                            { value: 'short', label: 'Bearish' },
                            { value: 'either', label: 'Either exposure' }
                        ]}
                        value={newRule.categoryLimitDirection}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                categoryLimitDirection: e.target.value as 'long' | 'short' | 'either'
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <FormSelect
                        id="categoryRuleLimitType"
                        label="Limit Type"
                        options={[
                            { value: 'exactCategoriesMatch', label: 'Exact Categories Match' },
                            { value: 'anyCategoriesMatch', label: 'Any Categories Match' },
                            { value: 'anyNonTopLevelCategoryMatch', label: 'Any Non-Top Level Category Match' }
                        ]}
                        value={newRule.categoryLimitType}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                categoryLimitType: e.target.value as
                                    | 'exactCategoriesMatch'
                                    | 'anyCategoriesMatch'
                                    | 'anyNonTopLevelCategoryMatch'
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <FormSelect
                        id="categoryRuleMaximumType"
                        label="Maximum Type"
                        options={[
                            { value: 'positions', label: 'Number of Positions' },
                            { value: 'value', label: 'Dollar Value' }
                        ]}
                        value={newRule.categoryLimitMaximumType}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                categoryLimitMaximumType: e.target.value as 'positions' | 'value'
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <FormNumberInput
                        id="categoryRuleValue"
                        label="Maximum Value"
                        min={0}
                        value={newRule.value ?? null}
                        onValueChange={(value) =>
                            setNewRule({
                                ...newRule,
                                value: value ?? undefined
                            })
                        }
                    />
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                    <Button className="mr-2" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveCategoryRule}>
                        {editingRuleId == null ? 'Add Rule' : 'Save Rule'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CategoryRuleModal;
